// src/components/forms/BookingForm.tsx

import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import {
  Box,
  Divider,
  Typography,
  Collapse,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  ContentCut,
  ErrorOutline as ErrorIcon,
  LocalOffer as PriceListIcon,
} from '@mui/icons-material';
import {
  Client,
  Pet,
  Booking,
  KennelSideOption,
  api,
  bookingsAPI,
} from '../../services/api';
import { colors } from '../../theme/theme';
import { FormErrors } from '../../utils/validationHelpers';
import { FIELD_MESSAGES } from '../../utils/validationMessages';
import CustomSelect from '../common/CustomSelect';
import CustomSelectMenuItem from '../common/CustomSelectMenuItem';
import {
  AddRowButton,
  AccountActionButton,
  CancelFormButton,
  SaveFormButton,
  StyledIconButton,
} from '../common/Buttons';
import { useGlobalToast } from '../../App';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  FormWrapper,
  FormContent,
  StickyFooter,
  StickyButtonContainer,
  FormFieldRow,
  FieldLabelContainer,
  FieldLabel,
  FieldInputContainer,
  FieldInputHeader,
  FieldInputHeaderIconContainer,
  StyledTextField,
  ResponsiveFormGrid,
  TopSpacerRow,
  BottomSpacerRow,
  FormFieldRowMultiline,
  FieldInputContainerMultiline,
} from './shared';
import LoadingSpinner from '../common/LoadingSpinner';
import FieldHelperText from '../forms/shared/FieldHelperText';
import FormMessageList from './shared/FormMessageList';
import DateRangePicker from './shared/DateRangePicker';
import PetSelectionRow from './shared/PetSelectionRow';
import SurchargeRow from './shared/SurchargeRow';
import DangerZoneRow from './shared/DangerZoneRow';
import ModalBase from '../common/modal/ModalBase';
import DeleteConfirmDialog from './shared/modal/DeleteConfirmDialog';
import PetStayManager from './shared/modal/PetStayManager';
import PetGroomingManager from './shared/modal/PetGroomingManager';
import KennelHeaterManager from './shared/modal/KennelHeaterManager';
import SurchargeManager from './shared/modal/SurchargeManager';
import CurrentPriceList from './shared/modal/CurrentPriceList';
import BookingSummary from './shared/BookingSummary';
import FormSkelly from './shared/FormSkelly';
import { getCustomIcon } from '../../utils/customIcons';
import { useBookingFormModals } from './hooks/useBookingFormModals';
import {
  BOOKING_STATUSES,
  BOOKING_FORM_STATUSES,
} from './bookingFormConstants';
import {
  getKennelSidePrefix,
  getKennelsForSelection,
  getSelectedKennelIds,
  hasAvailableUnselectedKennels,
  getFirstAvailableUnselectedKennel,
  clearUnavailableKennels,
  hasUnavailableKennels,
  sideAcceptsPetTypes,
  countSelectedRooms,
  getPlacementProblem,
  getPetTypeIcon,
  selectedOptionHasKennels,
  supportsHeater,
  Kennel,
  KennelSelection,
  PlacementItem,
} from './helpers/kennelHelpers';
import {
  calculateGroomingCount,
  buildBookingPetsPayload,
  SelectedPet,
} from './helpers/groomingHelpers';
import { SpacerRow } from '../forms/shared/SpacerRow';
import StartHereHint from '../common/StartHereHint';

interface BookingFormProps {
  client: Client;
  booking?: Booking;
  onSuccess: (savedBooking: Booking) => void;
  onCancel: () => void;
}

interface BookingFormDateData {
  dateRange?: string; // Range-level validation error
}

type BookingFormDateErrors = FormErrors<BookingFormDateData>;

interface Surcharge {
  id: number;
  reason: string;
  amount: number;
}

// Range-level validation for check-in/check-out dates.
const validateDateRange = (
  checkInDate: string,
  checkOutDate: string
): Partial<BookingFormDateData> => {
  const errors: Partial<BookingFormDateData> = {};

  // Only validate if both dates are present
  if (checkInDate && checkOutDate) {
    const checkIn = new Date(checkInDate);
    const checkOut = new Date(checkOutDate);

    // Check if check-out is after check-in
    if (checkOut <= checkIn) {
      errors.dateRange = 'Check-out date must be after check-in date';
      return errors;
    }

    const nights = Math.floor(
      (checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24)
    );

    // Maximum stay (max 365 nights)
    if (nights > 365) {
      errors.dateRange = 'Maximum stay is 365 nights';
      return errors;
    }
  }

  return errors;
};

const BookingForm: React.FC<BookingFormProps> = ({
  client,
  booking,
  onSuccess,
  onCancel,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { showSuccess, showError } = useGlobalToast();
  const { user } = useAuth();
  const showOnboarding = user?.showOnboarding ?? true;
  const navigate = useNavigate();
  const modal = useBookingFormModals();

  const nextIdRef = useRef(1);
  const getNextId = () => nextIdRef.current++;

  const isEditMode = Boolean(booking);
  const statusOptions: readonly string[] =
    booking?.status === 'Quotation' ? BOOKING_STATUSES : BOOKING_FORM_STATUSES;

  // Date fields
  const [checkInDate, setCheckInDate] = useState<string>('');
  const [checkOutDate, setCheckOutDate] = useState<string>('');
  const [errors, setErrors] = useState<BookingFormDateErrors>({});

  // Pets
  const [selectedPets, setSelectedPets] = useState<SelectedPet[]>([]);
  const [petsError, setPetsError] = useState<string>('');

  // Kennels
  const [kennelSelections, setKennelSelections] = useState<KennelSelection[]>([
    {
      id: getNextId(),
      kennelSideOptionId: null,
      kennelSideId: null,
      size: null,
      kennelId: null,
      heaterEnabled: false,
      heaterNights: 0,
    },
  ]);
  const [availableKennels, setAvailableKennels] = useState<Kennel[]>([]);
  const [availabilityKey, setAvailabilityKey] = useState<string | null>(null);
  const [availabilityFailed, setAvailabilityFailed] = useState(false);
  const knownKennelsRef = useRef<Map<number, Kennel>>(new Map());
  const [kennelSideOptions, setKennelSideOptions] = useState<
    KennelSideOption[]
  >([]);

  // Other
  const [notes, setNotes] = useState<string>('');
  const [notesExpanded, setNotesExpanded] = useState<boolean>(false);
  const [status, setStatus] = useState<string>('Pending');
  const [surcharges, setSurcharges] = useState<Surcharge[]>([]);
  const [depositAmount, setDepositAmount] = useState<string>('');

  // Pet and kennel data
  const [availablePets, setAvailablePets] = useState<Pet[]>([]);

  // Loading/submitting
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showFailureDialog, setShowFailureDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const kennelsSectionRef = useRef<HTMLDivElement>(null);
  const submitErrorRef = useRef<HTMLDivElement>(null);

  // System settings and UI state
  const [systemSettings, setSystemSettings] = useState<any>(null);
  const [showSummary, setShowSummary] = useState<boolean>(false);
  const [hintDismissed, setHintDismissed] = useState(false);
  const [kennelTypeTouched, setKennelTypeTouched] = useState(false);
  const [touchedKennelRowIds, setTouchedKennelRowIds] = useState<Set<number>>(
    new Set()
  );
  const [addKennelHintDone, setAddKennelHintDone] = useState(false);

  // Track if we've initialized edit mode data
  const editModeInitializedRef = useRef(false);

  // Only opens the collapse when the Done button is clicked, not on date selection.
  const [datesConfirmed, setDatesConfirmed] = useState(false);

  const areDatesSelected = Boolean(
    datesConfirmed && checkInDate && checkOutDate && !errors.dateRange
  );

  const currentAvailabilityKey =
    checkInDate && checkOutDate && !errors.dateRange
      ? `${checkInDate}|${checkOutDate}`
      : null;
  const isAvailabilityReady =
    currentAvailabilityKey !== null &&
    availabilityKey === currentAvailabilityKey;

  const numberOfDays = useMemo(() => {
    if (!checkInDate || !checkOutDate) return 0;

    const checkIn = new Date(checkInDate);
    const checkOut = new Date(checkOutDate);
    const diffTime = checkOut.getTime() - checkIn.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays > 0 ? diffDays : 0;
  }, [checkInDate, checkOutDate]);

  const selectedPetTypes = useMemo(() => {
    const found = new Map<number, string>();
    selectedPets
      .filter((p) => p.isSelected !== false)
      .forEach((p) => {
        const pet = availablePets.find((ap) => ap.id === p.petId);
        if (pet && typeof pet.petTypeId === 'number') {
          found.set(pet.petTypeId, pet.petType?.type || 'Unknown');
        }
      });
    return Array.from(found, ([id, name]) => ({ id, name }));
  }, [selectedPets, availablePets]);

  const selectedPetTypeIds = useMemo(
    () => selectedPetTypes.map((pt) => pt.id),
    [selectedPetTypes]
  );

  const selectedRoomCount = countSelectedRooms(kennelSelections);
  const placementProblem = useMemo(
    () =>
      getPlacementProblem(
        kennelSelections,
        kennelSideOptions,
        selectedPetTypes
      ),
    [kennelSelections, kennelSideOptions, selectedPetTypes]
  );
  const roomMissing = status !== 'Quotation' && selectedRoomCount === 0;
  const isKennelRuleBlocking = roomMissing || placementProblem !== null;

  // Load initial data: pets and kennel side options
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setIsLoading(true);

        const pets =
          client.pets?.filter((pet) => pet.stillOwned && !pet.isDeleted) || [];
        setAvailablePets(pets);

        const optionsResponse = await api.get<KennelSideOption[]>(
          '/Kennel/side-options'
        );
        setKennelSideOptions(optionsResponse.data);

        // Load system settings for heater rate
        try {
          const settingsResponse = await api.get<any>(
            '/ReferenceData/system-settings'
          );
          setSystemSettings(settingsResponse.data);
        } catch (settingsErr) {
          // Continue even if system settings fail
        }
      } catch (err: any) {
        showError('Failed to load booking form data');
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialData();
  }, [client, showError]);

  // Load edit mode dates first - no dependencies on other data
  useEffect(() => {
    if (isEditMode && booking && !editModeInitializedRef.current) {
      setCheckInDate(booking.checkInDate?.split('T')[0] || '');
      setCheckOutDate(booking.checkOutDate?.split('T')[0] || '');
      setStatus(booking.status || 'Pending');
      setNotes(booking.notes || '');
      setDepositAmount(
        (booking as any).depositAmount > 0
          ? parseFloat((booking as any).depositAmount).toFixed(2)
          : ''
      );
      setDatesConfirmed(true);

      editModeInitializedRef.current = true;
    }
  }, [isEditMode, booking]);

  // Reset form when transitioning from edit mode to new booking mode
  useEffect(() => {
    if (!isEditMode && editModeInitializedRef.current) {
      // Transitioning from edit to new mode, reset everything
      setCheckInDate('');
      setCheckOutDate('');
      setErrors({});
      setDatesConfirmed(false);
      setSelectedPets([]);
      setPetsError('');
      setKennelSelections([
        {
          id: getNextId(),
          kennelSideOptionId: null,
          kennelSideId: null,
          size: null,
          kennelId: null,
          heaterEnabled: false,
          heaterNights: 0,
        },
      ]);
      setNotes('');
      setNotesExpanded(false);
      setStatus('Pending');
      setSurcharges([]);
      setDepositAmount('');
      setShowSummary(false);

      editModeInitializedRef.current = false;
      hasAutoSelectedPets.current = false;
    }
  }, [isEditMode]);

  // Load pets AFTER availablePets are loaded
  useEffect(() => {
    if (
      isEditMode &&
      booking &&
      availablePets.length > 0 &&
      editModeInitializedRef.current
    ) {
      const pets = (booking as any).pets || [];
      if (pets.length > 0) {
        const bookingPets = pets.map((bookingPet: any) => ({
          petId: bookingPet.petId,
          petName: bookingPet.petName,
          petType: bookingPet.petType,
          rateType:
            (bookingPet.rateType as 'Single' | 'Sharing' | 'Custom') ||
            ('Single' as const),
          customRate: bookingPet.dailyRate,
          numberOfNights: bookingPet.numberOfNights,
          useSharingRate: bookingPet.useSharingRate,
          groomSmooth:
            bookingPet.grooming?.filter((g: any) => g.groomingType === 'Smooth')
              .length || 0,
          groomLong:
            bookingPet.grooming?.filter((g: any) => g.groomingType === 'Long')
              .length || 0,
          groomCut:
            bookingPet.grooming?.filter((g: any) => g.groomingType === 'Cut')
              .length || 0,
          lineItemDiscountPercentage:
            bookingPet.lineItemDiscountPercentage || 0,
          lineItemDiscount: bookingPet.lineItemDiscount || 0,
          isSelected: true, // Loaded pets are selected by default
        }));
        setSelectedPets(bookingPets);
      }
    }
  }, [isEditMode, booking, availablePets.length]);

  // Load kennels AFTER kennelSideOptions are ready
  useEffect(() => {
    if (isEditMode && booking && kennelSideOptions.length > 0) {
      const kennels = (booking as any).kennels || [];
      if (kennels.length > 0) {
        const bookingKennels = kennels.map((kennelData: any) => {
          // Use kennelSideId and size directly from API response
          // (no longer need to look up in availableKennels)
          const kennelSideId = kennelData.kennelSideId;
          const size = kennelData.size;

          // Find the KennelSideOption that matches this kennel's side and size
          const matchedOption = kennelSideOptions.find(
            (opt) => opt.kennelSideId === kennelSideId && opt.size === size
          );
          const kennelSideOptionId = matchedOption?.id || null;

          if (kennelData.kennelId) {
            knownKennelsRef.current.set(kennelData.kennelId, {
              id: kennelData.kennelId,
              kennelNumber: kennelData.kennelNumber,
              size: kennelData.size,
              kennelSideId: kennelData.kennelSideId,
            });
          }

          return {
            id: getNextId(),
            kennelSideOptionId,
            kennelSideId,
            size,
            kennelId: kennelData.kennelId,
            heaterEnabled: kennelData.heaterNights
              ? kennelData.heaterNights > 0
              : false,
            heaterNights: kennelData.heaterNights || 0,
          };
        });

        setKennelSelections(bookingKennels);
      } else {
        // Reset to default if no kennels
        setKennelSelections([
          {
            id: getNextId(),
            kennelSideOptionId: null,
            kennelSideId: null,
            size: null,
            kennelId: null,
            heaterEnabled: false,
            heaterNights: 0,
          },
        ]);
      }
    }
  }, [isEditMode, booking, kennelSideOptions]);

  // Load surcharges AFTER initialization
  useEffect(() => {
    if (isEditMode && booking && editModeInitializedRef.current) {
      const surcharges = (booking as any).surcharges || [];
      if (surcharges.length > 0) {
        const bookingSurcharges = surcharges.map((surcharge: any) => ({
          id: surcharge.id || getNextId(),
          reason: surcharge.reason,
          amount: surcharge.amount,
        }));
        setSurcharges(bookingSurcharges);
      }
    }
  }, [isEditMode, booking]);

  // Auto-select all pets on CREATE mode when dates are selected
  const hasAutoSelectedPets = useRef(false);
  useEffect(() => {
    if (
      !isEditMode &&
      areDatesSelected &&
      !hasAutoSelectedPets.current &&
      availablePets.length > 0 &&
      selectedPets.length === 0
    ) {
      const defaultSelectedPets: SelectedPet[] = availablePets.map((pet) => ({
        petId: pet.id,
        petName: pet.name,
        petType: pet.petType?.type || 'Unknown',
        rateType: 'Single',
        customRate: undefined,
        useSharingRate: false,
        groomSmooth: 0,
        groomLong: 0,
        groomCut: 0,
        lineItemDiscountPercentage: 0,
        lineItemDiscount: 0,
        isSelected: true, // Auto-selected pets are selected by default
      }));
      setSelectedPets(defaultSelectedPets);
      hasAutoSelectedPets.current = true;
    }
  }, [areDatesSelected, availablePets, isEditMode, selectedPets.length]);

  // Validates dates when they change (individual + range-level)
  const bookingId = booking?.id;
  useEffect(() => {
    let cancelled = false;

    const checkAvailability = async () => {
      setAvailabilityKey(null);
      setAvailabilityFailed(false);

      if (!checkInDate || !checkOutDate) {
        setAvailableKennels([]);
        setErrors({});
        return;
      }

      const rangeErrors = validateDateRange(checkInDate, checkOutDate);

      if (rangeErrors.dateRange) {
        setErrors((prev) => ({
          ...prev,
          dateRange: rangeErrors.dateRange,
        }));
        setAvailableKennels([]);
        return;
      }

      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.dateRange;
        return newErrors;
      });

      try {
        const response = await api.post<{ availableKennels: Kennel[] }>(
          '/Booking/CheckAvailability',
          {
            checkInDate,
            checkOutDate,
            ...(bookingId ? { excludeBookingId: bookingId } : {}),
          }
        );
        if (cancelled) return;
        response.data.availableKennels.forEach((k) =>
          knownKennelsRef.current.set(k.id, k)
        );
        setAvailableKennels(response.data.availableKennels);
        setAvailabilityKey(`${checkInDate}|${checkOutDate}`);
      } catch (err: any) {
        if (cancelled) return;
        showError('Failed to check kennel availability');
        setAvailableKennels([]);
        setAvailabilityFailed(true);
      }
    };

    checkAvailability();

    return () => {
      cancelled = true;
    };
  }, [checkInDate, checkOutDate, showError, bookingId]);

  const getKennelPrefix = useCallback(
    (selection: KennelSelection) =>
      getKennelSidePrefix(
        kennelSideOptions.find((o) => o.id === selection.kennelSideOptionId)
          ?.displayName || ''
      ),
    [kennelSideOptions]
  );

  // Clears any chosen kennel that the fresh availability no longer lists
  useEffect(() => {
    if (!isAvailabilityReady) return;
    setKennelSelections((prev) =>
      clearUnavailableKennels(prev, availableKennels, (sel) => {
        const kennel = knownKennelsRef.current.get(sel.kennelId!);
        return kennel ? `${getKennelPrefix(sel)}${kennel.kennelNumber}` : '';
      })
    );
  }, [
    isAvailabilityReady,
    availableKennels,
    kennelSelections,
    getKennelPrefix,
  ]);

  const getMiddleDividerSx = (borderStyle: string) => ({
    position: 'relative' as const,
    '&::after': {
      content: '""',
      position: 'absolute' as const,
      left: 0,
      right: 0,
      top: '50%',
      transform: 'translateY(-50%)',
      borderBottom: borderStyle,
      pointerEvents: 'none' as const,
      zIndex: 0,
      '@media (max-width: 600px)': {
        display: 'none',
      },
    },
  });

  const handleDateCheckInChange = (date: string) => {
    setCheckInDate(date);
    if (date) {
      setHintDismissed(true);
    }
    if (!date) {
      setDatesConfirmed(false);
    }
  };

  const handleDateCheckOutChange = (date: string) => {
    setCheckOutDate(date);
    if (!date) {
      setDatesConfirmed(false);
    }
  };

  useEffect(() => {
    if (checkInDate && checkOutDate && !errors.dateRange) {
      setDatesConfirmed(true);
    }
  }, [checkInDate, checkOutDate, errors.dateRange]);

  const togglePetSelection = (petId: number) => {
    const pet = availablePets.find((p) => p.id === petId);
    if (!pet) return;

    const selectedPet = selectedPets.find((p) => p.petId === petId);

    if (selectedPet) {
      // Toggle: set isSelected flag instead of removing pet
      setSelectedPets(
        selectedPets.map((p) =>
          p.petId === petId ? { ...p, isSelected: !selectedPet.isSelected } : p
        )
      );
    } else {
      // New pet: add with isSelected: true (default)
      setSelectedPets([
        ...selectedPets,
        {
          petId: pet.id,
          petName: pet.name,
          petType: pet.petType?.type || 'Unknown',
          rateType: 'Single',
          customRate: undefined,
          useSharingRate: false,
          groomSmooth: 0,
          groomLong: 0,
          groomCut: 0,
          lineItemDiscountPercentage: 0,
          lineItemDiscount: 0,
          isSelected: true,
        },
      ]);
      setPetsError('');
    }
  };

  // Pet stay modal handlers
  const handleSaveStayDuration = useCallback(
    (
      duration: number,
      rateType: 'Single' | 'Sharing' | 'Custom',
      customRate?: number,
      lineItemDiscountPercentage?: number
    ) => {
      setSelectedPets((prev) => {
        const updated = prev.map((pet) => {
          if (pet.petId !== modal.editingItemId) return pet;

          // Calculate discount amount from percentage
          let dailyRate = 0;
          if (rateType === 'Custom' && customRate) {
            dailyRate = customRate;
          } else {
            const petData = availablePets.find((p) => p.id === pet.petId);
            if (rateType === 'Sharing' && petData?.petSize?.priceSharing) {
              dailyRate = petData.petSize.priceSharing;
            } else if (petData?.petSize?.priceSingle) {
              dailyRate = petData.petSize.priceSingle;
            }
          }

          const staySubtotal = dailyRate * duration;
          const discountPercentage = lineItemDiscountPercentage || 0;
          const discountAmount = (staySubtotal * discountPercentage) / 100;

          return {
            ...pet,
            numberOfNights: duration,
            rateType,
            customRate,
            useSharingRate: rateType === 'Sharing',
            lineItemDiscountPercentage: discountPercentage,
            lineItemDiscount: discountAmount,
          };
        });

        if (duration === 0) {
          return updated.filter((p) => p.petId !== modal.editingItemId);
        }

        return updated;
      });

      modal.closeModal();
    },
    [modal, availablePets]
  );

  const handleEditStayDuration = useCallback(
    (petId: number) => {
      modal.openModal('pet-stay', petId);
    },
    [modal]
  );

  // Grooming modal handlers
  const handleSaveGrooming = useCallback(
    (smooth: number, long: number, cut: number) => {
      setSelectedPets((prev) =>
        prev.map((pet) =>
          pet.petId === modal.editingItemId
            ? { ...pet, groomSmooth: smooth, groomLong: long, groomCut: cut }
            : pet
        )
      );

      modal.closeModal();
    },
    [modal]
  );

  const handleEditGrooming = useCallback(
    (petId: number) => {
      modal.openModal('pet-grooming', petId);
    },
    [modal]
  );

  // Global rate modal handlers
  const handleOpenGlobalRateModal = useCallback(() => {
    modal.openModal('global-rate');
  }, [modal]);

  // Price list modal handler
  const handleOpenPriceListModal = useCallback(() => {
    modal.openModal('price-list');
  }, [modal]);

  const handleSaveGlobalRate = useCallback(
    (
      duration: number,
      rateType: 'Single' | 'Sharing' | 'Custom',
      customRate?: number,
      lineItemDiscountPercentage?: number
    ) => {
      setSelectedPets((prev) =>
        prev.map((pet) => {
          let dailyRate = 0;
          if (rateType === 'Custom' && customRate) {
            dailyRate = customRate;
          } else {
            const petData = availablePets.find((p) => p.id === pet.petId);
            if (rateType === 'Sharing' && petData?.petSize?.priceSharing) {
              dailyRate = petData.petSize.priceSharing;
            } else if (petData?.petSize?.priceSingle) {
              dailyRate = petData.petSize.priceSingle;
            }
          }

          const duration = pet.numberOfNights || numberOfDays;
          const staySubtotal = dailyRate * duration;
          const discountPercentage = lineItemDiscountPercentage || 0;
          const discountAmount = (staySubtotal * discountPercentage) / 100;

          return {
            ...pet,
            rateType,
            customRate,
            useSharingRate: rateType === 'Sharing',
            lineItemDiscountPercentage: discountPercentage,
            lineItemDiscount: discountAmount,
          };
        })
      );
      modal.closeModal();
    },
    [modal, availablePets, numberOfDays]
  );

  // Global heater modal handlers
  const handleOpenGlobalHeaterModal = useCallback(() => {
    modal.openModal('global-heater');
  }, [modal]);

  const handleSaveGlobalHeater = (
    heaterEnabled: boolean,
    heaterNights: number
  ) => {
    setKennelSelections((prev) =>
      prev.map((sel) => ({
        ...sel,
        heaterEnabled,
        heaterNights,
      }))
    );
    modal.closeModal();
  };

  // Kennel handlers
  const handleKennelSideOptionChange = (selectionId: number, value: string) => {
    const optionId = parseInt(value);
    const selectedOption = kennelSideOptions.find((o) => o.id === optionId);

    if (!selectedOption) return;

    setKennelSelections(
      kennelSelections.map((sel) =>
        sel.id === selectionId
          ? {
              ...sel,
              kennelSideOptionId: optionId,
              kennelSideId: selectedOption.kennelSideId,
              size: selectedOption.size,
              kennelId: null,
              unavailableKennelLabel: undefined,
            }
          : sel
      )
    );
  };

  const handleKennelChange = useCallback(
    (selectionId: number, kennelId: number) => {
      setKennelSelections(
        kennelSelections.map((sel) =>
          sel.id === selectionId
            ? { ...sel, kennelId, unavailableKennelLabel: undefined }
            : sel
        )
      );
    },
    [kennelSelections]
  );

  const handleAddKennel = () => {
    setKennelSelections([
      ...kennelSelections,
      {
        id: getNextId(),
        kennelSideOptionId: null,
        kennelSideId: null,
        size: null,
        kennelId: null,
        heaterEnabled: false,
        heaterNights: 0,
      },
    ]);
  };

  const handleRemoveKennel = (selectionId: number) => {
    if (kennelSelections.length > 1) {
      setKennelSelections(
        kennelSelections.filter((sel) => sel.id !== selectionId)
      );
    }
  };

  const handleOpenHeaterModal = (selectionId: number) => {
    modal.openModal('kennel-heater', selectionId);
  };

  const handleSaveHeater = (heaterEnabled: boolean, heaterNights: number) => {
    setKennelSelections(
      kennelSelections.map((sel) =>
        sel.id === modal.editingItemId
          ? { ...sel, heaterEnabled, heaterNights }
          : sel
      )
    );
    modal.closeModal();
  };

  // Surcharge handlers
  const handleOpenSurchargeModal = useCallback(() => {
    modal.openModal('surcharge');
  }, [modal]);

  const handleEditSurcharge = useCallback(
    (id: number) => {
      modal.openModal('surcharge', id);
    },
    [modal]
  );

  const handleSurchargeManagerSave = useCallback(
    (reason: string, amount: number) => {
      if (!reason.trim()) {
        showError('Please enter a reason for the surcharge');
        return;
      }
      if (amount <= 0) {
        showError('Please enter an amount greater than 0');
        return;
      }

      if (modal.editingItemId !== null) {
        setSurcharges((prev) =>
          prev.map((s) =>
            s.id === modal.editingItemId ? { ...s, reason, amount } : s
          )
        );
      } else {
        const newId = Math.max(...surcharges.map((s) => s.id), 0) + 1;
        setSurcharges((prev) => [
          ...prev,
          {
            id: newId,
            reason,
            amount,
          },
        ]);
      }

      modal.closeModal();
    },
    [modal, surcharges, showError]
  );

  const handleRemoveSurcharge = useCallback((id: number) => {
    setSurcharges((prev) => prev.filter((s) => s.id !== id));
  }, []);

  // Notes handlers
  const handleClearNotes = useCallback(() => {
    setNotes('');
    setNotesExpanded(false);
  }, []);

  // Auto-assign kennels when side option is selected
  useEffect(() => {
    kennelSelections.forEach((sel) => {
      if (
        isAvailabilityReady &&
        sel.kennelSideOptionId &&
        !sel.kennelId &&
        sel.unavailableKennelLabel === undefined &&
        sel.size !== null &&
        availableKennels.length > 0
      ) {
        const unselectedKennel = getFirstAvailableUnselectedKennel(
          sel.kennelSideId,
          sel.size,
          kennelSelections,
          availableKennels,
          sel.id
        );
        if (unselectedKennel) {
          handleKennelChange(sel.id, unselectedKennel.id);
        }
      }
    });
  }, [
    kennelSelections,
    availableKennels,
    handleKennelChange,
    isAvailabilityReady,
  ]);

  // Form validation & submission

  const isFormValid = (): boolean => {
    if (!checkInDate || !checkOutDate) return false;

    // Check for range-level validation errors
    if (errors.dateRange) {
      return false;
    }

    // Count only pets with isSelected === true
    const selectedPetCount = selectedPets.filter(
      (p) => p.isSelected !== false
    ).length;

    if (selectedPetCount === 0) {
      setPetsError(FIELD_MESSAGES.petsRequired);
      return false;
    }

    setPetsError('');

    const kennelIds = kennelSelections
      .filter((sel) => sel.kennelId !== null)
      .map((sel) => sel.kennelId!);

    const uniqueKennelIds = new Set(kennelIds);
    if (kennelIds.length !== uniqueKennelIds.size) {
      showError('Cannot select the same kennel more than once');
      return false;
    }

    return true;
  };

  const isKennelCheckBlocking =
    !isAvailabilityReady || hasUnavailableKennels(kennelSelections);

  const kennelGate: {
    error: boolean;
    text?: string;
    advisory?: boolean;
    missing?: PlacementItem[];
  } | null = (() => {
    if (hasUnavailableKennels(kennelSelections)) {
      return {
        error: true,
        text: 'Fix the kennels marked above before saving.',
      };
    }
    if (availabilityFailed) {
      return {
        error: true,
        text: 'Kennel availability could not be checked. Change the dates to try again.',
      };
    }
    if (!isAvailabilityReady) {
      return { error: false, text: 'Checking kennel availability...' };
    }
    if (placementProblem?.kind === 'missing') {
      return { error: false, missing: placementProblem.items };
    }
    if (placementProblem) {
      return { error: false, advisory: true, text: placementProblem.text };
    }
    return null;
  })();

  const lastKennelSelection = kennelSelections[kennelSelections.length - 1];
  const showAddKennelButton = Boolean(
    lastKennelSelection?.kennelSideOptionId &&
    (lastKennelSelection.kennelId ||
      !selectedOptionHasKennels(lastKennelSelection))
  );
  const missingRooms =
    areDatesSelected && kennelGate?.missing ? kennelGate.missing : null;
  const lastRowAwaitingType =
    kennelSelections.length > 1 &&
    !lastKennelSelection.kennelSideOptionId &&
    !touchedKennelRowIds.has(lastKennelSelection.id);
  const showRoomHints =
    showOnboarding && missingRooms !== null && !isSubmitting;
  const showAddKennelHint =
    showRoomHints && showAddKennelButton && !addKennelHintDone;

  const markKennelRowTouched = (selectionId: number) => {
    setKennelTypeTouched(true);
    setTouchedKennelRowIds((prev) =>
      prev.has(selectionId) ? prev : new Set(prev).add(selectionId)
    );
  };

  const handleFailureDialogClose = () => {
    setShowFailureDialog(false);
    const toKennels = submitError !== null && /kennel|room/i.test(submitError);
    const target = toKennels
      ? kennelsSectionRef.current
      : submitErrorRef.current;
    target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // The dialog hands focus back to the button that opened it as it closes,
    // so focus moves a moment later.
    window.setTimeout(() => {
      const focusTarget = toKennels
        ? kennelsSectionRef.current?.querySelector<HTMLElement>(
            '[role="combobox"]'
          )
        : submitErrorRef.current;
      focusTarget?.focus({ preventScroll: true });
    }, 350);
  };

  const handleSubmit = async () => {
    setSubmitError(null);

    if (isKennelCheckBlocking) {
      showError('Kennel availability must be confirmed before saving');
      return;
    }

    if (!isFormValid()) {
      // Check selected pets count
      const selectedPetCount = selectedPets.filter(
        (p) => p.isSelected !== false
      ).length;
      if (selectedPetCount === 0) {
        return;
      }
      showError('Please complete all required fields');
      return;
    }

    if (errors.dateRange) {
      showError('Please fix date validation errors');
      return;
    }

    try {
      setIsSubmitting(true);

      const petsData = buildBookingPetsPayload(selectedPets);

      const kennels = kennelSelections
        .filter((sel) => sel.kennelId !== null && sel.kennelId !== undefined)
        .map((sel) => ({
          kennelId: sel.kennelId!,
          heaterNights: sel.heaterNights || 0,
        }));

      const kennelSideIds = kennelSelections
        .filter((sel) => sel.kennelSideId !== null && sel.size === null)
        .map((sel) => sel.kennelSideId!);

      // Compute subtotal to mirror backend: pets + heaters + surcharges
      const VAT_RATE = 0.15;
      const computedSubtotal =
        selectedPets
          .filter((p) => p.isSelected !== false)
          .reduce((sum, pet) => {
            const petData = availablePets.find((ap) => ap.id === pet.petId);
            const nights = pet.numberOfNights || numberOfDays;
            let rate = 0;
            if (pet.rateType === 'Custom' && pet.customRate) {
              rate = pet.customRate;
            } else if (pet.rateType === 'Sharing') {
              rate = petData?.petSize?.priceSharing || 0;
            } else {
              rate = petData?.petSize?.priceSingle || 0;
            }
            const accommodation = rate * nights;
            const grooming =
              (pet.groomSmooth || 0) *
                (petData?.petSize?.priceGroomingSmooth || 0) +
              (pet.groomLong || 0) *
                (petData?.petSize?.priceGroomingLong || 0) +
              (pet.groomCut || 0) * (petData?.petSize?.priceCut || 0);
            return sum + accommodation - (pet.lineItemDiscount || 0) + grooming;
          }, 0) +
        kennelSelections
          .filter((sel) => sel.kennelId != null)
          .reduce(
            (sum, sel) =>
              sum +
              (sel.heaterNights || 0) * (systemSettings?.heaterDailyRate || 0),
            0
          ) +
        surcharges.reduce((sum, s) => sum + s.amount, 0);

      const taxAmount =
        Math.round((computedSubtotal - computedSubtotal / 1.15) * 100) / 100;

      const bookingData = {
        clientId: client.id,
        checkInDate,
        checkOutDate,
        pets: petsData,
        kennels,
        kennelSideIds,
        status,
        notes,
        discountAmount: 0,
        taxAmount,
        depositAmount: parseFloat(depositAmount) || 0,
        surcharges: surcharges.map((s) => ({
          reason: s.reason,
          amount: s.amount,
        })),
        ...(isEditMode &&
          booking?.rowVersion && { rowVersion: booking.rowVersion }),
      };

      let savedBooking: Booking;

      if (isEditMode && booking) {
        savedBooking = await bookingsAPI.update(booking.id, bookingData);
        showSuccess();
      } else {
        savedBooking = (await api.post<Booking>('/Booking', bookingData)).data;
        showSuccess();
      }

      onSuccess(savedBooking);
    } catch (err: any) {
      setSubmitError(err.response?.data?.message || 'Failed to save booking');
      setShowFailureDialog(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteBooking = async () => {
    if (!booking) return;
    setIsDeleting(true);
    try {
      await bookingsAPI.delete(booking.id);
      showSuccess();
      navigate(`/clients/${client.id}/bookings`);
    } catch {
      showError('Failed to delete booking');
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  // Render helpers

  const getFirstSelectedPetRate = (): {
    rateType: 'Single' | 'Sharing' | 'Custom';
    customRate?: number;
  } => {
    // Find first selected pet (with isSelected !== false)
    const firstSelectedPet = selectedPets.find((p) => p.isSelected !== false);
    if (!firstSelectedPet) {
      return { rateType: 'Single' };
    }
    return {
      rateType: firstSelectedPet.rateType || 'Single',
      customRate: firstSelectedPet.customRate,
    };
  };

  const renderPetStayModal = () => {
    if (!modal.isOpen('pet-stay') || modal.editingItemId === null) return null;

    const petData = availablePets.find((p) => p.id === modal.editingItemId);
    const priceSingle = petData?.petSize?.priceSingle;
    const priceSharing = petData?.petSize?.priceSharing;
    const selectedPet = selectedPets.find(
      (p) => p.petId === modal.editingItemId
    );

    return (
      <ModalBase
        mobileWidth="92vw"
        open={true}
        onClose={modal.closeModal}
        petName={selectedPet?.petName || ''}
        headerIcon={<EditIcon />}
        headerTitle={`Edit ${selectedPet?.petName || ''}`}
      >
        <PetStayManager
          petId={modal.editingItemId}
          rateType={selectedPet?.rateType || 'Single'}
          customRate={selectedPet?.customRate}
          lineItemDiscount={selectedPet?.lineItemDiscountPercentage || 0}
          currentDuration={selectedPet?.numberOfNights || numberOfDays}
          maxDuration={numberOfDays}
          bookingLength={numberOfDays}
          priceSingle={priceSingle}
          priceSharing={priceSharing}
          onSave={handleSaveStayDuration}
          onCancel={modal.closeModal}
          isLoading={isSubmitting}
        />
      </ModalBase>
    );
  };

  const renderPetGroomingModal = () => {
    if (!modal.isOpen('pet-grooming') || modal.editingItemId === null)
      return null;

    const selectedPet = selectedPets.find(
      (p) => p.petId === modal.editingItemId
    );

    return (
      <ModalBase
        mobileWidth="92vw"
        open={true}
        onClose={modal.closeModal}
        petName={selectedPet?.petName || ''}
        headerIcon={<ContentCut />}
        headerTitle={`Groom ${selectedPet?.petName || ''}`}
      >
        <PetGroomingManager
          petId={modal.editingItemId}
          groomSmooth={selectedPet?.groomSmooth || 0}
          groomLong={selectedPet?.groomLong || 0}
          groomCut={selectedPet?.groomCut || 0}
          onSave={handleSaveGrooming}
          onCancel={modal.closeModal}
          isLoading={isSubmitting}
        />
      </ModalBase>
    );
  };

  const renderGlobalRateModal = () => {
    // Find first selected pet (with isSelected !== false)
    const firstSelectedPet = selectedPets.find((p) => p.isSelected !== false);
    const firstPetData = availablePets.find(
      (p) => p.id === firstSelectedPet?.petId
    );
    const priceSingle = firstPetData?.petSize?.priceSingle;
    const priceSharing = firstPetData?.petSize?.priceSharing;
    const firstPetRate = getFirstSelectedPetRate();

    return (
      <ModalBase
        open={modal.isOpen('global-rate')}
        onClose={modal.closeModal}
        mobileWidth="92vw"
        petName="All Pets"
        headerIcon={<EditIcon />}
        headerTitle="Set Rate for All Pets"
      >
        <PetStayManager
          petId={0}
          rateType={firstPetRate.rateType}
          customRate={firstPetRate.customRate}
          currentDuration={numberOfDays}
          maxDuration={numberOfDays}
          bookingLength={numberOfDays}
          priceSingle={priceSingle}
          priceSharing={priceSharing}
          onSave={handleSaveGlobalRate}
          onCancel={modal.closeModal}
          isLoading={isSubmitting}
          hideStayLength={true}
          hideDiscount={false}
          hideRate={true}
        />
      </ModalBase>
    );
  };

  const renderGlobalHeaterModal = () => {
    return (
      <ModalBase
        open={modal.isOpen('global-heater')}
        onClose={modal.closeModal}
        mobileWidth="92vw"
        petName="All Kennels"
        headerIcon={getCustomIcon('heater')}
        headerTitle="All Heaters"
      >
        <KennelHeaterManager
          kennelSelectionId={0}
          heaterEnabled={true}
          heaterNights={numberOfDays}
          bookingLength={numberOfDays}
          onSave={handleSaveGlobalHeater}
          onCancel={modal.closeModal}
          isLoading={isSubmitting}
        />
      </ModalBase>
    );
  };

  const renderKennelHeaterModal = () => {
    if (!modal.isOpen('kennel-heater') || modal.editingItemId === null)
      return null;

    const currentSelection = kennelSelections.find(
      (sel) => sel.id === modal.editingItemId
    );
    const currentOption = kennelSideOptions.find(
      (opt) => opt.id === currentSelection?.kennelSideOptionId
    );
    const displayName = currentOption?.displayName || '';
    const kennelSide = getKennelSidePrefix(displayName);

    return (
      <ModalBase
        mobileWidth="92vw"
        open={true}
        onClose={modal.closeModal}
        petName="Kennel Settings"
        headerIcon={getCustomIcon('heater')}
        headerTitle="Heater"
        kennelNumber={
          availableKennels.find(
            (k) =>
              k.id ===
              kennelSelections.find((sel) => sel.id === modal.editingItemId)
                ?.kennelId
          )?.kennelNumber
        }
        kennelSide={kennelSide}
      >
        <KennelHeaterManager
          kennelSelectionId={modal.editingItemId}
          heaterEnabled={
            kennelSelections.find((sel) => sel.id === modal.editingItemId)
              ?.heaterEnabled || false
          }
          heaterNights={
            kennelSelections.find((sel) => sel.id === modal.editingItemId)
              ?.heaterNights || 0
          }
          bookingLength={numberOfDays}
          onSave={handleSaveHeater}
          onCancel={modal.closeModal}
          isLoading={isSubmitting}
        />
      </ModalBase>
    );
  };

  const renderSurchargeModal = () => {
    return (
      <ModalBase
        open={modal.isOpen('surcharge')}
        onClose={modal.closeModal}
        mobileWidth="92vw"
        petName="Surcharge"
        headerIcon={getCustomIcon('money')}
        headerTitle={
          modal.editingItemId !== null ? 'Edit Surcharge' : 'Add Surcharge'
        }
      >
        <SurchargeManager
          currentReason={
            modal.editingItemId !== null
              ? surcharges.find((s) => s.id === modal.editingItemId)?.reason ||
                ''
              : ''
          }
          currentAmount={
            modal.editingItemId !== null
              ? surcharges.find((s) => s.id === modal.editingItemId)?.amount ||
                0
              : 0
          }
          onSave={handleSurchargeManagerSave}
          onCancel={modal.closeModal}
          isLoading={isSubmitting}
        />
      </ModalBase>
    );
  };

  const renderPriceListModal = () => {
    return (
      <CurrentPriceList
        open={modal.isOpen('price-list')}
        onClose={modal.closeModal}
      />
    );
  };
  const bottomSpacerHeight = areDatesSelected ? '28rem' : '48rem';

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <FormWrapper>
      <FormContent>
        <ResponsiveFormGrid>
          <TopSpacerRow datesSelected={areDatesSelected} />

          {/* DateRangePicker Component */}
          <FormFieldRowMultiline>
            <FieldLabelContainer>
              <FieldLabel>
                {isMobile ? (
                  <>
                    Check-in / Check-out
                    <span className="required">*</span>
                  </>
                ) : (
                  <Box
                    component="span"
                    sx={{ display: 'flex', flexDirection: 'column' }}
                  >
                    <Box component="span" height={'3.3rem'}>
                      Check-in<span className="required">*</span>
                    </Box>
                    <Box component="span" height={'1rem'}></Box>
                    <Box component="span" height={'3.3rem'}>
                      Check-out<span className="required">*</span>
                    </Box>
                  </Box>
                )}
              </FieldLabel>
            </FieldLabelContainer>
            <FieldInputContainerMultiline
              sx={{ paddingLeft: '1rem', position: 'relative' }}
            >
              {!isEditMode && !hintDismissed && showOnboarding && (
                <StartHereHint
                  color={colors.secondary.main}
                  loops={6}
                  targetSelector='[aria-label*="calendar"]'
                />
              )}
              <DateRangePicker
                desktopType="modal"
                checkInDate={checkInDate}
                checkOutDate={checkOutDate}
                onCheckInChange={handleDateCheckInChange}
                onCheckOutChange={handleDateCheckOutChange}
                rangeError={Boolean(errors.dateRange)}
                rangeHelperText={errors.dateRange}
                disabled={isSubmitting}
                title="Select Dates..."
              />
            </FieldInputContainerMultiline>
          </FormFieldRowMultiline>
          {/* FormSkelly goes here */}
          <Collapse in={Boolean(!areDatesSelected)} timeout={300}>
            <FormFieldRow>
              <FieldLabelContainer />
              <FieldInputContainer
                sx={{
                  maxWidth: '740px',
                  [theme.breakpoints.down('sm')]: {
                    padding: '0.15rem 0',
                  },
                }}
              >
                {availablePets.length > 0 && (
                  <FormSkelly availablePets={availablePets} />
                )}
              </FieldInputContainer>
            </FormFieldRow>
          </Collapse>
          {/* Length display */}
          <Collapse in={Boolean(areDatesSelected)} timeout={300}>
            <FormFieldRow>
              <FieldLabelContainer>
                <FieldLabel sx={{ display: { xs: 'none', sm: 'block' } }}>
                  Length
                </FieldLabel>
              </FieldLabelContainer>
              <FieldInputContainer sx={{ paddingLeft: '1rem' }}>
                <Typography
                  sx={{
                    fontSize: '1.8rem',
                    fontWeight: 700,
                    color: areDatesSelected ? 'text.primary' : 'text.disabled',
                    display: { xs: 'block', sm: 'none' },
                  }}
                >
                  Length: {numberOfDays} {numberOfDays === 1 ? 'Day' : 'Days'}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '1.8rem',
                    fontWeight: 700,
                    color: areDatesSelected ? 'text.primary' : 'text.disabled',
                    display: { xs: 'none', sm: 'block' },
                  }}
                >
                  {numberOfDays} {numberOfDays === 1 ? 'Day' : 'Days'}
                </Typography>
              </FieldInputContainer>
            </FormFieldRow>
          </Collapse>

          {/* Pets Section */}
          <Collapse in={Boolean(areDatesSelected)} timeout={300}>
            <Box>
              <SpacerRow
                height="2rem"
                labelHeightMobile="1rem"
                valueHeightMobile="1rem"
                labelBorderMobile={{
                  top: 'none',
                  bottom: 'none',
                }}
                valueSx={getMiddleDividerSx(
                  `3px dashed ${theme.palette.dividerContrast}`
                )}
                valueBorderMobile={{
                  top: `3px dashed ${theme.palette.dividerContrast}`,
                  bottom: 'none',
                }}
                labelBackgroundColor={
                  theme.palette.mode === 'dark'
                    ? theme.palette.grey[750]
                    : theme.palette.grey[400]
                }
                labelBackgroundColorMobile={
                  theme.palette.mode === 'dark'
                    ? theme.palette.grey[750]
                    : theme.palette.grey[300]
                }
              />
              {availablePets.length > 0 && (
                <>
                  <FormFieldRow>
                    <FieldLabelContainer />
                    <FieldInputContainer
                      sx={{
                        [theme.breakpoints.down('sm')]: {
                          padding: '0.15rem 0',
                        },
                      }}
                    >
                      <FieldInputHeader
                        sx={{
                          backgroundColor: `${theme.palette.primary.main}`,
                          padding: '0.125rem 1rem 0.125rem 0',
                          [theme.breakpoints.down('sm')]: {
                            padding: '0.125rem 0',
                          },
                        }}
                      >
                        <FieldLabel
                          sx={{
                            fontSize: '2rem',
                            fontWeight: '700',
                            paddingLeft: '1rem',
                            color: colors.text.light,
                            [theme.breakpoints.down('sm')]: {
                              paddingLeft: '1rem',
                            },
                          }}
                        >
                          Pets<span className="requiredLight">*</span>
                        </FieldLabel>
                        <FieldInputHeaderIconContainer gap="0.75rem">
                          {selectedPets.filter((p) => p.isSelected !== false)
                            .length > 0 && (
                            <StyledIconButton
                              onClick={handleOpenGlobalRateModal}
                              hoverBackgroundColor={colors.text.light}
                              hoverColor={theme.palette.primary.main}
                              disabled={
                                isSubmitting ||
                                selectedPets.filter(
                                  (p) => p.isSelected !== false
                                ).length === 0
                              }
                              size="small"
                              title="Set rate for all pets"
                              sx={{
                                width: '3rem',
                                height: '3rem',
                                transition: 'opacity 0.2s ease',
                                '& svg': {
                                  fontSize: '2rem',
                                  color: `${colors.text.light}`,
                                },
                              }}
                            >
                              <EditIcon sx={{ fontSize: '1.8rem' }} />
                            </StyledIconButton>
                          )}
                          <StyledIconButton
                            onClick={handleOpenPriceListModal}
                            hoverBackgroundColor={colors.text.light}
                            hoverColor={theme.palette.primary.main}
                            variant="bold"
                            disabled={isSubmitting}
                            size="small"
                            title="View current prices"
                            sx={{
                              width: '3rem',
                              height: '3rem',
                              marginRight: '1rem',
                              transition: 'opacity 0.2s ease',
                              '& svg': {
                                fontSize: '2rem',
                                color: `${colors.text.light}`,
                              },
                            }}
                          >
                            <PriceListIcon sx={{ fontSize: '1.8rem' }} />
                          </StyledIconButton>
                        </FieldInputHeaderIconContainer>
                      </FieldInputHeader>
                    </FieldInputContainer>
                  </FormFieldRow>

                  <FormFieldRow>
                    <FieldLabelContainer />
                    <FieldInputContainer>
                      <Box sx={{ width: '100%' }}>
                        {availablePets.map((pet) => {
                          const selectedPet = selectedPets.find(
                            (p) => p.petId === pet.id
                          );
                          // Check isSelected flag (default true for backwards compatibility)
                          const isSelected = selectedPet
                            ? selectedPet.isSelected !== false
                            : false;
                          const groomingCount = selectedPet
                            ? calculateGroomingCount(selectedPet)
                            : 0;
                          const stayLength = selectedPet?.numberOfNights;

                          return (
                            <PetSelectionRow
                              key={pet.id}
                              petId={pet.id}
                              petName={pet.name}
                              petType={pet.petType?.type || 'Unknown'}
                              petBreed={pet.petBreed?.breed}
                              isSelected={isSelected}
                              onToggle={togglePetSelection}
                              disabled={isSubmitting}
                              numberOfNights={stayLength}
                              bookingLength={numberOfDays}
                              useSharingRate={
                                selectedPet?.useSharingRate || false
                              }
                              useCustomRate={selectedPet?.rateType === 'Custom'}
                              groomingCount={groomingCount}
                              stayLength={stayLength}
                              priceSingle={pet.petSize?.priceSingle}
                              priceSharing={pet.petSize?.priceSharing}
                              priceGroomingSmooth={
                                pet.petSize?.priceGroomingSmooth
                              }
                              priceGroomingLong={pet.petSize?.priceGroomingLong}
                              priceCut={pet.petSize?.priceCut}
                              discountPercentage={
                                selectedPet?.lineItemDiscountPercentage || 0
                              }
                              onEditClick={handleEditStayDuration}
                              onGroomingClick={handleEditGrooming}
                            />
                          );
                        })}
                      </Box>
                      {petsError && (
                        <Box sx={{ mt: 1 }}>
                          <FieldHelperText
                            error={true}
                            helperText={petsError}
                          />
                        </Box>
                      )}
                    </FieldInputContainer>
                  </FormFieldRow>
                </>
              )}
              <SpacerRow
                height="1rem"
                labelHeightMobile="0"
                valueHeightMobile="1rem"
                labelBorderMobile={{
                  top: 'none',
                  bottom: 'none',
                }}
                valueBorder={{
                  top: `3px dashed ${theme.palette.dividerContrast}`,
                }}
                valueBorderMobile={{
                  top: `3px dashed ${theme.palette.dividerContrast}`,
                  bottom: 'none',
                }}
                labelBackgroundColor={
                  theme.palette.mode === 'dark'
                    ? theme.palette.grey[750]
                    : theme.palette.grey[400]
                }
                labelBackgroundColorMobile={
                  theme.palette.mode === 'dark'
                    ? theme.palette.grey[750]
                    : theme.palette.grey[300]
                }
              />
            </Box>
          </Collapse>

          {/* Modals: Pet Stay, Grooming, Rates, Price List */}
          {renderPetStayModal()}
          {renderPetGroomingModal()}
          {renderGlobalRateModal()}
          {renderPriceListModal()}

          {/* Kennels Section */}
          <Collapse in={Boolean(areDatesSelected)} timeout={300}>
            <Box ref={kennelsSectionRef}>
              <FormFieldRow>
                <FieldLabelContainer />
                <FieldInputContainer
                  sx={{
                    [theme.breakpoints.down('sm')]: {
                      padding: '0.15rem 0',
                    },
                  }}
                >
                  <FieldInputHeader
                    sx={{
                      backgroundColor: `${theme.palette.primary.main}`,
                      padding: '0.125rem 2rem 0.125rem 0',
                      [theme.breakpoints.down('sm')]: {
                        padding: '0.125rem 1rem 0.125rem 0',
                      },
                    }}
                  >
                    <FieldLabel
                      sx={{
                        fontSize: '2rem',
                        fontWeight: '700',
                        paddingLeft: '1rem',
                        paddingRight: '2rem',
                        color: colors.text.light,
                        flex: 1,
                        minWidth: 0,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        maskImage:
                          'linear-gradient(to right, #000 calc(100% - 2rem), transparent)',
                        WebkitMaskImage:
                          'linear-gradient(to right, #000 calc(100% - 2rem), transparent)',
                      }}
                    >
                      {`Kennels - ${selectedRoomCount} selected`}
                    </FieldLabel>
                    <FieldInputHeaderIconContainer>
                      <StyledIconButton
                        variant="bold"
                        onClick={handleOpenGlobalHeaterModal}
                        hoverBackgroundColor={colors.text.light}
                        hoverColor={theme.palette.primary.main}
                        disabled={isSubmitting}
                        size="small"
                        title="Set heater for all kennels"
                        sx={{
                          width: '3rem',
                          height: '3rem',
                          transition: 'opacity 0.2s ease',
                          '& svg': {
                            fontSize: '2rem',
                            color: `${colors.text.light}`,
                          },
                        }}
                      >
                        <EditIcon />
                      </StyledIconButton>
                    </FieldInputHeaderIconContainer>
                  </FieldInputHeader>
                </FieldInputContainer>
              </FormFieldRow>

              {kennelSelections.map((selection, index) => {
                const kennelsForSelection = getKennelsForSelection(
                  selection.kennelSideId,
                  selection.size,
                  availableKennels
                );
                const selectedKennelIds = getSelectedKennelIds(
                  kennelSelections,
                  selection.id
                );
                const isLastRow = index === kennelSelections.length - 1;
                const optionHasKennels = selectedOptionHasKennels(selection);
                const isFlagged =
                  selection.unavailableKennelLabel !== undefined;
                const suggestedKennel = isFlagged
                  ? getFirstAvailableUnselectedKennel(
                      selection.kennelSideId,
                      selection.size,
                      kennelSelections,
                      availableKennels,
                      selection.id
                    )
                  : null;
                const suggestedLabel = suggestedKennel
                  ? `${getKennelPrefix(selection)}${suggestedKennel.kennelNumber}`
                  : '';
                const kennelNoun =
                  getKennelPrefix(selection) === 'C' ? 'Cattery' : 'Kennel';
                const unavailableMessage = `${
                  selection.unavailableKennelLabel
                    ? `${kennelNoun} ${selection.unavailableKennelLabel}`
                    : `The selected ${kennelNoun.toLowerCase()}`
                } is booked for these dates.${
                  suggestedKennel
                    ? ''
                    : ' No other kennel of this type is free. Choose another type or change the dates.'
                }`;

                return (
                  <React.Fragment key={selection.id}>
                    <FormFieldRow>
                      <FieldLabelContainer>
                        <FieldLabel>
                          {kennelSelections.length === 1
                            ? ''
                            : `Kennel ${index + 1}`}
                        </FieldLabel>
                      </FieldLabelContainer>
                      <FieldInputContainer sx={{ paddingLeft: '1rem' }}>
                        <Box
                          onClickCapture={() =>
                            markKennelRowTouched(selection.id)
                          }
                          onFocusCapture={() =>
                            markKennelRowTouched(selection.id)
                          }
                          sx={{
                            display: 'flex',
                            gap: 1,
                            alignItems: 'center',
                            width: '100%',
                          }}
                        >
                          {index === 0 &&
                            showOnboarding &&
                            areDatesSelected &&
                            roomMissing &&
                            !kennelTypeTouched &&
                            !hasUnavailableKennels(kennelSelections) && (
                              <StartHereHint
                                color={colors.secondary.main}
                                loops={3}
                                targetSelector='[role="combobox"][aria-label^="Kennel"] svg'
                              />
                            )}
                          {isLastRow &&
                            index > 0 &&
                            showRoomHints &&
                            lastRowAwaitingType && (
                              <StartHereHint
                                color={colors.secondary.main}
                                loops={3}
                                targetSelector={`[role="combobox"][aria-label^="Kennel ${index + 1} type"] svg`}
                              />
                            )}
                          <CustomSelect
                            value={
                              selection.kennelSideOptionId?.toString() || ''
                            }
                            onChange={(value) =>
                              handleKennelSideOptionChange(selection.id, value)
                            }
                            ariaLabel={
                              kennelSelections.length === 1
                                ? 'Kennel type'
                                : `Kennel ${index + 1} type`
                            }
                            label=""
                            placeholder="Select..."
                            disabled={isSubmitting}
                            width="100%"
                            maxWidth="600px"
                          >
                            {kennelSideOptions.map((option) => {
                              const acceptsPets = sideAcceptsPetTypes(
                                option.allowedPetTypeIds,
                                selectedPetTypeIds
                              );
                              const kennelsAvailable =
                                option.size === null
                                  ? true
                                  : hasAvailableUnselectedKennels(
                                      option.kennelSideId,
                                      option.size,
                                      kennelSelections,
                                      availableKennels,
                                      selection.id
                                    );
                              return (
                                <CustomSelectMenuItem
                                  key={option.displayName}
                                  value={option.id.toString()}
                                  disabled={!kennelsAvailable || !acceptsPets}
                                >
                                  {(() => {
                                    const displayText =
                                      option.displayName.replace(
                                        'Cattery Standard',
                                        'Cattery'
                                      );
                                    return kennelsAvailable || !acceptsPets
                                      ? displayText
                                      : `${displayText} - none available`;
                                  })()}
                                </CustomSelectMenuItem>
                              );
                            })}
                          </CustomSelect>

                          {index > 0 && (
                            <StyledIconButton
                              variant="delete"
                              onClick={() => handleRemoveKennel(selection.id)}
                              disabled={isSubmitting}
                              color="error"
                              sx={{
                                '& svg': {
                                  fontSize: '2.75rem',
                                },
                              }}
                              title="Remove kennel"
                            >
                              <AddIcon
                                sx={{
                                  transform: 'rotate(45deg)',
                                }}
                              />
                            </StyledIconButton>
                          )}
                        </Box>
                      </FieldInputContainer>
                    </FormFieldRow>

                    {selection.kennelSideOptionId && optionHasKennels && (
                      <FormFieldRow>
                        <FieldLabelContainer></FieldLabelContainer>
                        <FieldInputContainer sx={{ paddingLeft: '1rem' }}>
                          <Box
                            sx={{
                              display: 'flex',
                              gap: 1,
                              alignItems: 'center',
                              width: '100%',
                            }}
                          >
                            <CustomSelect
                              key={`kennel-select-${selection.id}-${selection.kennelSideOptionId}`}
                              value={selection.kennelId?.toString() || ''}
                              ariaLabel={
                                kennelSelections.length === 1
                                  ? 'Kennel'
                                  : `Kennel ${index + 1}`
                              }
                              onChange={(value) =>
                                handleKennelChange(
                                  selection.id,
                                  parseInt(value)
                                )
                              }
                              label=""
                              placeholder="Select..."
                              disabled={
                                isSubmitting || kennelsForSelection.length === 0
                              }
                              width="100%"
                              maxWidth="600px"
                            >
                              {kennelsForSelection.map((kennel) => {
                                const isAlreadySelected = selectedKennelIds.has(
                                  kennel.id
                                );

                                const currentOption = kennelSideOptions.find(
                                  (opt) =>
                                    opt.id === selection.kennelSideOptionId
                                );
                                const displayName =
                                  currentOption?.displayName || '';
                                const kennelSidePrefix =
                                  getKennelSidePrefix(displayName);

                                return (
                                  <CustomSelectMenuItem
                                    key={kennel.id}
                                    value={kennel.id.toString()}
                                    disabled={isAlreadySelected}
                                  >
                                    {kennelSidePrefix}
                                    {kennel.kennelNumber}
                                    {isAlreadySelected && ' - already selected'}
                                  </CustomSelectMenuItem>
                                );
                              })}
                            </CustomSelect>

                            {supportsHeater(
                              kennelSideOptions.find(
                                (opt) => opt.id === selection.kennelSideOptionId
                              )?.displayName || ''
                            ) ? (
                              <StyledIconButton
                                variant="bold"
                                onClick={() =>
                                  handleOpenHeaterModal(selection.id)
                                }
                                disabled={isSubmitting || !selection.kennelId}
                                sx={{
                                  flexShrink: 0,
                                }}
                                title="Configure heater"
                              >
                                <EditIcon
                                  sx={{
                                    fontSize: '1.8rem',
                                    color: selection.heaterEnabled
                                      ? 'primary.main'
                                      : 'inherit',
                                  }}
                                />
                              </StyledIconButton>
                            ) : null}
                          </Box>
                          {kennelsForSelection.length === 0 && !isFlagged && (
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                mt: 0.5,
                                color: 'secondary.main',
                                fontSize: '1.8rem',
                              }}
                            >
                              No kennels available for selected dates
                            </Typography>
                          )}
                          {isFlagged && (
                            <Box sx={{ width: '100%', maxWidth: '790px' }}>
                              <FieldHelperText
                                error
                                helperText={unavailableMessage}
                              />
                              {suggestedKennel && (
                                <Box
                                  sx={{
                                    mt: 1,
                                    ml: '0.5rem',
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    alignItems: 'center',
                                    gap: '1rem',
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: '2rem',
                                      fontWeight: 700,
                                      color:
                                        theme.palette.mode === 'dark'
                                          ? theme.palette.error.light
                                          : theme.palette.error.main,
                                    }}
                                  >
                                    {`Suggested: ${suggestedLabel}.`}
                                  </Typography>
                                  <AccountActionButton
                                    variant="outlined"
                                    data-use-kennel={selection.id}
                                    disabled={isSubmitting}
                                    minWidth="156px"
                                    onClick={() =>
                                      handleKennelChange(
                                        selection.id,
                                        suggestedKennel.id
                                      )
                                    }
                                  >
                                    {`Use ${suggestedLabel}`}
                                  </AccountActionButton>
                                  {showOnboarding && (
                                    <StartHereHint
                                      color={colors.secondary.main}
                                      loops={3}
                                      side="right"
                                      targetSelector={`[data-use-kennel="${selection.id}"]`}
                                    />
                                  )}
                                </Box>
                              )}
                            </Box>
                          )}
                        </FieldInputContainer>
                      </FormFieldRow>
                    )}

                    {(selection.kennelId || !optionHasKennels) &&
                      selection.kennelSideOptionId &&
                      !isLastRow && (
                        <>
                          <SpacerRow
                            height="2rem"
                            labelHeightMobile="1rem"
                            valueHeightMobile="1rem"
                            labelBorderMobile={{
                              top: 'none',
                              bottom: 'none',
                            }}
                            valueSx={getMiddleDividerSx(
                              `3px dashed ${theme.palette.dividerContrast}`
                            )}
                            valueBorderMobile={{
                              top: `3px dashed ${theme.palette.dividerContrast}`,
                              bottom: 'none',
                            }}
                            labelBackgroundColor={
                              theme.palette.mode === 'dark'
                                ? theme.palette.grey[750]
                                : theme.palette.grey[400]
                            }
                            labelBackgroundColorMobile={
                              theme.palette.mode === 'dark'
                                ? theme.palette.grey[750]
                                : theme.palette.grey[300]
                            }
                          />
                        </>
                      )}
                  </React.Fragment>
                );
              })}

              {(showAddKennelButton || missingRooms) && (
                <FormFieldRow>
                  <FieldLabelContainer />
                  <FieldInputContainer sx={{ paddingLeft: '1rem' }}>
                    <FormMessageList
                      heading="Still required:"
                      headingUnderline
                      items={(missingRooms ?? []).map((room) => ({
                        label: room.label,
                        icon: getPetTypeIcon(room.petTypeId),
                      }))}
                      action={
                        showAddKennelButton && (
                          <>
                            <AddRowButton
                              onClick={handleAddKennel}
                              disabled={isSubmitting}
                              title="Add additional kennel"
                            />
                            {showAddKennelHint && (
                              <StartHereHint
                                color={colors.secondary.main}
                                loops={3}
                                targetSelector='[title="Add additional kennel"]'
                                onFinish={() => setAddKennelHintDone(true)}
                                backdrop={
                                  isMobile
                                    ? theme.palette.mode === 'dark'
                                      ? theme.palette.grey[750]
                                      : theme.palette.grey[300]
                                    : undefined
                                }
                              />
                            )}
                          </>
                        )
                      }
                    />
                  </FieldInputContainer>
                </FormFieldRow>
              )}

              {areDatesSelected && kennelGate && !kennelGate.missing && (
                <FormFieldRow>
                  <FieldLabelContainer />
                  <FieldInputContainer paddingLeft={'.5rem'}>
                    <FieldHelperText
                      error={kennelGate.error}
                      helperText={kennelGate.text}
                      multiline={kennelGate.advisory}
                      fontWeight={kennelGate.advisory ? 700 : undefined}
                      fontSize={kennelGate.advisory ? '2.2rem' : undefined}
                      color={kennelGate.advisory ? 'error' : undefined}
                    />
                  </FieldInputContainer>
                </FormFieldRow>
              )}

              <SpacerRow
                height="2rem"
                labelHeightMobile="1rem"
                valueHeightMobile="1rem"
                labelBorderMobile={{
                  top: 'none',
                  bottom: 'none',
                }}
                valueSx={getMiddleDividerSx(
                  `3px dashed ${theme.palette.dividerContrast}`
                )}
                valueBorderMobile={{
                  top: `3px dashed ${theme.palette.dividerContrast}`,
                  bottom: 'none',
                }}
                labelBackgroundColor={
                  theme.palette.mode === 'dark'
                    ? theme.palette.grey[750]
                    : theme.palette.grey[400]
                }
                labelBackgroundColorMobile={
                  theme.palette.mode === 'dark'
                    ? theme.palette.grey[750]
                    : theme.palette.grey[300]
                }
              />
            </Box>
          </Collapse>

          {/* Modals: Heaters */}
          {renderGlobalHeaterModal()}
          {renderKennelHeaterModal()}

          {/* Status & Surcharges & Notes */}
          <Collapse in={Boolean(areDatesSelected)} timeout={300}>
            <Box>
              {/* Surcharges */}
              <FormFieldRow>
                <FieldLabelContainer />
                <FieldInputContainer
                  sx={{
                    [theme.breakpoints.down('sm')]: {
                      padding: '0.15rem 0',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      maxWidth: '800px',
                    }}
                  >
                    {surcharges.length === 0 ? (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          pb: '0.4rem',
                          '@media (max-width: 600px)': {
                            pr: '1rem',
                          },
                        }}
                      >
                        <Typography
                          onClick={handleOpenSurchargeModal}
                          sx={{
                            color: 'primary.main',
                            fontSize: '1.8rem',
                            fontWeight: 500,
                            cursor: 'pointer',
                            '&:hover': {
                              textDecoration: 'underline',
                              color: 'secondary.main',
                            },
                          }}
                        >
                          Add Surcharge
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        <FieldInputHeader
                          sx={{
                            backgroundColor: `${theme.palette.primary.main}`,
                            padding: '0.125rem 1rem 0.125rem 0',
                            [theme.breakpoints.down('sm')]: {
                              padding: '0.125rem 0',
                            },
                          }}
                        >
                          <FieldLabel
                            sx={{
                              fontSize: '2rem',
                              fontWeight: '700',
                              paddingLeft: '1rem',
                              color: colors.text.light,
                              [theme.breakpoints.down('sm')]: {
                                paddingLeft: '1rem',
                              },
                            }}
                          >
                            Surcharges
                          </FieldLabel>
                        </FieldInputHeader>
                        <Box
                          sx={{
                            width: '100%',
                            padding: '0.125rem 1rem 0.125rem 0',
                            [theme.breakpoints.down('sm')]: {
                              padding: '0.5rem 0.5rem 0 0.5rem',
                            },
                          }}
                        >
                          {surcharges.map((surcharge) => (
                            <SurchargeRow
                              key={surcharge.id}
                              surchargeId={surcharge.id}
                              reason={surcharge.reason}
                              amount={surcharge.amount}
                              onEdit={handleEditSurcharge}
                              onDelete={handleRemoveSurcharge}
                              disabled={isSubmitting}
                            />
                          ))}
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            width: '100%',
                            maxWidth: '800px',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            mt: 1,
                          }}
                        >
                          <AddRowButton
                            onClick={handleOpenSurchargeModal}
                            disabled={isSubmitting}
                            title="Add surcharge"
                          />
                        </Box>
                      </>
                    )}
                  </Box>
                </FieldInputContainer>
              </FormFieldRow>

              {renderSurchargeModal()}

              <SpacerRow
                height="2rem"
                labelHeightMobile="1rem"
                valueHeightMobile="1rem"
                labelBorderMobile={{
                  top: 'none',
                  bottom: 'none',
                }}
                valueSx={getMiddleDividerSx(
                  `3px dashed ${theme.palette.dividerContrast}`
                )}
                valueBorderMobile={{
                  top: `3px dashed ${theme.palette.dividerContrast}`,
                  bottom: 'none',
                }}
                labelBackgroundColor={
                  theme.palette.mode === 'dark'
                    ? theme.palette.grey[750]
                    : theme.palette.grey[400]
                }
                labelBackgroundColorMobile={
                  theme.palette.mode === 'dark'
                    ? theme.palette.grey[750]
                    : theme.palette.grey[300]
                }
              />

              {/* Total */}
              <FormFieldRow>
                <FieldLabelContainer />
                <FieldInputContainer
                  sx={{
                    [theme.breakpoints.down('sm')]: {
                      padding: '0.15rem 0',
                    },
                  }}
                >
                  <FieldInputHeader
                    sx={{
                      backgroundColor: `${theme.palette.primary.main}`,
                      padding: '0.125rem 1rem 0.125rem 0',
                      [theme.breakpoints.down('sm')]: {
                        padding: '0.125rem 0',
                      },
                    }}
                  >
                    <FieldLabel
                      sx={{
                        fontSize: '2rem',
                        fontWeight: '700',
                        paddingLeft: '1rem',
                        color: colors.text.light,
                        [theme.breakpoints.down('sm')]: {
                          paddingLeft: '1rem',
                        },
                      }}
                    >
                      Total
                    </FieldLabel>
                  </FieldInputHeader>
                  <FieldInputContainer
                    sx={{
                      paddingLeft: '1rem',
                      padding: '1rem 0 0 1rem',
                      [theme.breakpoints.down('sm')]: {
                        paddingTop: '1rem',
                        paddingLeft: '1rem',
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '2rem',
                        fontWeight: 700,
                        color: theme.palette.text.primary,
                      }}
                    >
                      {(() => {
                        const VAT_RATE = 0.15;
                        const petSubtotal = selectedPets.reduce((sum, pet) => {
                          const petData = availablePets.find(
                            (p) => p.id === pet.petId
                          );
                          const nights = pet.numberOfNights || numberOfDays;
                          let rate = 0;
                          if (pet.rateType === 'Custom' && pet.customRate)
                            rate = pet.customRate;
                          else if (
                            pet.rateType === 'Sharing' &&
                            petData?.petSize?.priceSharing
                          )
                            rate = petData.petSize.priceSharing;
                          else if (petData?.petSize?.priceSingle)
                            rate = petData.petSize.priceSingle;
                          const grooming =
                            (pet.groomSmooth || 0) *
                              (petData?.petSize?.priceGroomingSmooth || 0) +
                            (pet.groomLong || 0) *
                              (petData?.petSize?.priceGroomingLong || 0) +
                            (pet.groomCut || 0) *
                              (petData?.petSize?.priceCut || 0);
                          return (
                            sum +
                            rate * nights -
                            (pet.lineItemDiscount || 0) +
                            grooming
                          );
                        }, 0);
                        const heaterRate = systemSettings?.heaterDailyRate || 0;
                        const kennelSubtotal = kennelSelections
                          .filter((s) => s.kennelId !== null)
                          .reduce(
                            (sum, s) =>
                              sum + (s.heaterNights || 0) * heaterRate,
                            0
                          );
                        const surchargeSubtotal = surcharges.reduce(
                          (sum, s) => sum + s.amount,
                          0
                        );
                        const subtotal =
                          petSubtotal + kennelSubtotal + surchargeSubtotal;
                        const grandTotal = subtotal;
                        return `R${grandTotal.toFixed(2)}`;
                      })()}
                    </Typography>
                  </FieldInputContainer>
                </FieldInputContainer>
              </FormFieldRow>

              <SpacerRow
                height="2rem"
                labelHeightMobile="1rem"
                valueHeightMobile="1rem"
                labelBorderMobile={{
                  top: 'none',
                  bottom: 'none',
                }}
                valueSx={getMiddleDividerSx(
                  `3px dashed ${theme.palette.dividerContrast}`
                )}
                valueBorderMobile={{
                  top: `3px dashed ${theme.palette.dividerContrast}`,
                  bottom: 'none',
                }}
                labelBackgroundColor={
                  theme.palette.mode === 'dark'
                    ? theme.palette.grey[750]
                    : theme.palette.grey[400]
                }
                labelBackgroundColorMobile={
                  theme.palette.mode === 'dark'
                    ? theme.palette.grey[750]
                    : theme.palette.grey[300]
                }
              />

              <FormFieldRow>
                <FieldLabelContainer />
                <FieldInputContainer
                  sx={{
                    [theme.breakpoints.down('sm')]: {
                      padding: '0.15rem 0',
                    },
                  }}
                >
                  <FieldInputHeader
                    sx={{
                      backgroundColor: `${theme.palette.primary.main}`,
                      padding: '0.125rem 1rem 0.125rem 0',
                      [theme.breakpoints.down('sm')]: {
                        padding: '0.125rem 0',
                      },
                    }}
                  >
                    <FieldLabel
                      id="booking-deposit-label"
                      sx={{
                        fontSize: '2rem',
                        fontWeight: '700',
                        paddingLeft: '1rem',
                        color: colors.text.light,
                        [theme.breakpoints.down('sm')]: {
                          paddingLeft: '1rem',
                        },
                      }}
                    >
                      Deposit
                    </FieldLabel>
                  </FieldInputHeader>
                  <FieldInputContainer
                    sx={{
                      paddingLeft: '1rem',
                      padding: '1rem 0 0 1rem',
                      [theme.breakpoints.down('sm')]: {
                        paddingTop: '1rem',
                        paddingLeft: '1rem',
                      },
                    }}
                  >
                    <StyledTextField
                      id="booking-deposit"
                      inputProps={{
                        inputMode: 'decimal',
                        'aria-labelledby': 'booking-deposit-label',
                      }}
                      value={depositAmount}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const val = e.target.value;
                        if (val === '' || /^\d*\.?\d{0,2}$/.test(val)) {
                          setDepositAmount(val);
                        }
                      }}
                      onBlur={() => {
                        if (
                          depositAmount !== '' &&
                          !isNaN(parseFloat(depositAmount))
                        ) {
                          setDepositAmount(
                            parseFloat(depositAmount).toFixed(2)
                          );
                        }
                      }}
                      onFocus={() => {
                        if (
                          depositAmount !== '' &&
                          depositAmount.endsWith('.00')
                        ) {
                          setDepositAmount(
                            parseFloat(depositAmount).toString()
                          );
                        }
                      }}
                      placeholder="0.00"
                      disabled={isSubmitting}
                      sx={{ maxWidth: '600px', width: '100%' }}
                    />
                  </FieldInputContainer>
                </FieldInputContainer>
              </FormFieldRow>

              <SpacerRow
                height="2rem"
                labelHeightMobile="1rem"
                valueHeightMobile="1rem"
                labelBorderMobile={{
                  top: 'none',
                  bottom: 'none',
                }}
                valueSx={getMiddleDividerSx(
                  `3px dashed ${theme.palette.dividerContrast}`
                )}
                valueBorderMobile={{
                  top: `3px dashed ${theme.palette.dividerContrast}`,
                  bottom: 'none',
                }}
                labelBackgroundColor={
                  theme.palette.mode === 'dark'
                    ? theme.palette.grey[750]
                    : theme.palette.grey[400]
                }
                labelBackgroundColorMobile={
                  theme.palette.mode === 'dark'
                    ? theme.palette.grey[750]
                    : theme.palette.grey[300]
                }
              />

              {/* Status - visible in edit mode only; auto-set via deposit in create mode */}
              {isEditMode && (
                <FormFieldRow>
                  <FieldLabelContainer />
                  <FieldInputContainer
                    sx={{
                      [theme.breakpoints.down('sm')]: {
                        padding: '0.15rem 0',
                      },
                    }}
                  >
                    <FieldInputHeader
                      sx={{
                        backgroundColor: `${theme.palette.primary.main}`,
                        padding: '0.125rem 1rem 0.125rem 0',
                        [theme.breakpoints.down('sm')]: {
                          padding: '0.125rem 0',
                        },
                      }}
                    >
                      <FieldLabel
                        sx={{
                          fontSize: '2rem',
                          fontWeight: '700',
                          paddingLeft: '1rem',
                          color: colors.text.light,
                          [theme.breakpoints.down('sm')]: {
                            paddingLeft: '1rem',
                          },
                        }}
                      >
                        Status
                      </FieldLabel>
                    </FieldInputHeader>
                    <FieldInputContainer
                      sx={{
                        paddingLeft: '1rem',
                        padding: '1rem 0 0 1rem',
                        [theme.breakpoints.down('sm')]: {
                          paddingTop: '1rem',
                          paddingLeft: '1rem',
                        },
                      }}
                    >
                      <CustomSelect
                        value={status}
                        onChange={setStatus}
                        label=""
                        ariaLabel="Status"
                        disabled={isSubmitting}
                        width="100%"
                        maxWidth="600px"
                        placeholder=""
                      >
                        {statusOptions.map((statusOption) => (
                          <CustomSelectMenuItem
                            key={statusOption}
                            value={statusOption}
                          >
                            {statusOption}
                          </CustomSelectMenuItem>
                        ))}
                      </CustomSelect>
                    </FieldInputContainer>
                  </FieldInputContainer>
                </FormFieldRow>
              )}

              {isEditMode && (
                <SpacerRow
                  height="2rem"
                  labelHeightMobile="1rem"
                  valueHeightMobile="1rem"
                  labelBorderMobile={{
                    top: 'none',
                    bottom: 'none',
                  }}
                  valueSx={getMiddleDividerSx(
                    `3px dashed ${theme.palette.dividerContrast}`
                  )}
                  valueBorderMobile={{
                    top: `3px dashed ${theme.palette.dividerContrast}`,
                    bottom: 'none',
                  }}
                  labelBackgroundColor={
                    theme.palette.mode === 'dark'
                      ? theme.palette.grey[750]
                      : theme.palette.grey[400]
                  }
                  labelBackgroundColorMobile={
                    theme.palette.mode === 'dark'
                      ? theme.palette.grey[750]
                      : theme.palette.grey[300]
                  }
                />
              )}

              {/* Notes */}
              <FormFieldRow>
                <FieldLabelContainer />
                <FieldInputContainer
                  sx={{
                    [theme.breakpoints.down('sm')]: {
                      padding: '0.15rem 0',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      maxWidth: '800px',
                    }}
                  >
                    {notes.trim().length === 0 && !notesExpanded ? (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          pb: '0.4rem',
                          '@media (max-width: 600px)': {
                            pr: '1rem',
                          },
                        }}
                      >
                        <Typography
                          onClick={() => setNotesExpanded(true)}
                          sx={{
                            color: 'primary.main',
                            fontSize: '1.8rem',
                            fontWeight: 500,
                            cursor: 'pointer',
                            '&:hover': {
                              textDecoration: 'underline',
                              color: 'secondary.main',
                            },
                          }}
                        >
                          Add Notes
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        <FieldInputHeader
                          sx={{
                            backgroundColor: `${theme.palette.primary.main}`,
                            padding: '0.125rem 1rem 0.125rem 0',
                            [theme.breakpoints.down('sm')]: {
                              padding: '0.125rem 0',
                            },
                          }}
                        >
                          <FieldLabel
                            id="booking-notes-label"
                            sx={{
                              fontSize: '2rem',
                              fontWeight: '700',
                              paddingLeft: '1rem',
                              color: colors.text.light,
                              [theme.breakpoints.down('sm')]: {
                                paddingLeft: '1rem',
                              },
                            }}
                          >
                            Notes
                          </FieldLabel>
                          <FieldInputHeaderIconContainer>
                            <StyledIconButton
                              variant="delete"
                              onClick={handleClearNotes}
                              hoverBackgroundColor={colors.text.light}
                              hoverColor={theme.palette.primary.main}
                              disabled={isSubmitting}
                              color="error"
                              sx={{
                                mr: '1rem',
                                '& svg': {
                                  fontSize: '2.75rem',
                                  color: `${colors.text.light}`,
                                },
                                [theme.breakpoints.down('sm')]: {
                                  mr: '1rem',
                                },
                              }}
                              title="Clear notes"
                            >
                              <AddIcon
                                sx={{
                                  transform: 'rotate(45deg)',
                                }}
                              />
                            </StyledIconButton>
                          </FieldInputHeaderIconContainer>
                        </FieldInputHeader>

                        <FieldInputContainer
                          sx={{
                            paddingLeft: '1rem',
                            [theme.breakpoints.down('sm')]: {
                              paddingLeft: 0,
                            },
                          }}
                        ></FieldInputContainer>
                        <Box
                          sx={{
                            position: 'relative',
                            width: '100%',
                            mb: '0.5rem',
                            paddingLeft: '1rem',
                            [theme.breakpoints.down('sm')]: {
                              padding: '1rem',
                            },
                          }}
                        >
                          <StyledTextField
                            id="booking-notes"
                            inputProps={{
                              'aria-labelledby': 'booking-notes-label',
                            }}
                            fullWidth
                            multiline
                            rows={2}
                            value={notes}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) => setNotes(e.target.value)}
                            disabled={isSubmitting}
                          />
                        </Box>
                        <FieldInputContainer />
                      </>
                    )}
                  </Box>
                </FieldInputContainer>
              </FormFieldRow>

              <SpacerRow
                height="2rem"
                labelHeightMobile="1rem"
                valueHeightMobile="1rem"
                labelBorderMobile={{
                  top: 'none',
                  bottom: 'none',
                }}
                valueSx={getMiddleDividerSx(
                  `3px dashed ${theme.palette.dividerContrast}`
                )}
                valueBorderMobile={{
                  top: `3px dashed ${theme.palette.dividerContrast}`,
                  bottom: 'none',
                }}
                labelBackgroundColor={
                  theme.palette.mode === 'dark'
                    ? theme.palette.grey[750]
                    : theme.palette.grey[400]
                }
                labelBackgroundColorMobile={
                  theme.palette.mode === 'dark'
                    ? theme.palette.grey[750]
                    : theme.palette.grey[300]
                }
              />

              {/* Booking Summary Toggle & Display */}
              <Collapse in={!showSummary} timeout={300}>
                <FormFieldRow>
                  <FieldLabelContainer />
                  <FieldInputContainer>
                    <Box
                      sx={{
                        width: '100%',
                        maxWidth: '800px',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                        }}
                      >
                        <Typography
                          onClick={() => setShowSummary(true)}
                          sx={{
                            color: 'primary.main',
                            fontSize: '1.8rem',
                            fontWeight: 500,
                            cursor: 'pointer',
                            '&:hover': {
                              textDecoration: 'underline',
                              color: 'secondary.main',
                            },
                          }}
                        >
                          Booking Summary
                        </Typography>
                      </Box>
                    </Box>
                  </FieldInputContainer>
                </FormFieldRow>
              </Collapse>

              {/* Booking Summary Component */}
              <Collapse in={showSummary} timeout={300}>
                <FormFieldRow>
                  <FieldLabelContainer />
                  <FieldInputContainer>
                    <BookingSummary
                      checkInDate={checkInDate}
                      checkOutDate={checkOutDate}
                      numberOfDays={numberOfDays}
                      selectedPets={selectedPets.filter(
                        (p) => p.isSelected !== false
                      )}
                      availablePets={availablePets}
                      kennelSelections={kennelSelections}
                      availableKennels={availableKennels}
                      kennelSideOptions={kennelSideOptions}
                      surcharges={surcharges}
                      notes={notes}
                      status={status}
                      depositAmount={parseFloat(depositAmount) || 0}
                      systemSettings={systemSettings}
                      onClose={() => setShowSummary(false)}
                    />
                  </FieldInputContainer>
                </FormFieldRow>
              </Collapse>
            </Box>
          </Collapse>

          {submitError && (
            <FormFieldRow>
              <FieldLabelContainer>
                <FieldLabel></FieldLabel>
              </FieldLabelContainer>
              <FieldInputContainer>
                <Box
                  ref={submitErrorRef}
                  role="alert"
                  tabIndex={-1}
                  sx={{
                    width: '100%',
                    maxWidth: '800px',
                    mt: '3rem',
                    p: '1rem 1.5rem',
                    border: `3px solid ${theme.palette.error.main}`,
                    borderRadius: '4px',
                    outline: 'none',
                  }}
                >
                  <FieldHelperText
                    error
                    helperText={`${isEditMode ? 'Update' : 'Create'} Failed: ${submitError}`}
                  />
                </Box>
              </FieldInputContainer>
            </FormFieldRow>
          )}

          {isEditMode && booking && (
            <DangerZoneRow
              label="Delete Booking"
              onClick={() => setShowDeleteDialog(true)}
              disabled={isSubmitting}
            />
          )}

          <BottomSpacerRow minHeight={bottomSpacerHeight} />
        </ResponsiveFormGrid>
      </FormContent>

      <StickyFooter>
        <StickyButtonContainer>
          <CancelFormButton
            minWidth="278px"
            sx={{
              minWidth: { xs: '175px', sm: '278px' },
            }}
            onClick={onCancel}
            disabled={isSubmitting}
          />
          <SaveFormButton
            isSubmitting={isSubmitting}
            text={isEditMode ? 'Update' : 'Create'}
            onClick={handleSubmit}
            minWidth="278px"
            disabled={
              !areDatesSelected || isKennelCheckBlocking || isKennelRuleBlocking
            }
            sx={{
              minWidth: { xs: '165px', sm: '278px' },
            }}
          />
        </StickyButtonContainer>
      </StickyFooter>

      <ModalBase
        open={showFailureDialog}
        onClose={handleFailureDialogClose}
        mobileWidth="92vw"
        petName="Booking"
        headerIcon={<ErrorIcon />}
        headerTitle="Booking not saved"
      >
        <Box sx={{ p: '1rem 0' }}>
          <Typography sx={{ fontSize: '2rem', fontWeight: 700, mb: '2rem' }}>
            {submitError}
          </Typography>
          <AccountActionButton
            variant="outlined"
            minWidth="156px"
            onClick={handleFailureDialogClose}
          >
            OK
          </AccountActionButton>
        </Box>
      </ModalBase>

      <DeleteConfirmDialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDeleteBooking}
        isDeleting={isDeleting}
        title="Delete Booking"
        message={
          <>
            This will delete <strong>Booking #{booking?.id}</strong> and its
            associated invoice and payments. This action cannot be undone from
            here.
          </>
        }
      />
    </FormWrapper>
  );
};

export default BookingForm;
